import React from 'react';
import { View, StyleSheet } from 'react-native';
import { SkeletonBone } from '../SkeletonBone';

type TransactionRowVariant = 'row' | 'card' | 'table-row';

interface TransactionRowSkeletonProps {
  variant?: TransactionRowVariant;
}

/** Row variant: [40x40 icon] + [title + date] + [amount + method] */
const RowVariant: React.FC = () => (
  <View style={styles.row}>
    <SkeletonBone width={40} height={40} borderRadius={10} />
    <View style={styles.rowContent}>
      <SkeletonBone width={140} height={14} />
      <SkeletonBone width={80} height={12} />
    </View>
    <View style={styles.rowAmountCol}>
      <SkeletonBone width={70} height={14} />
      <SkeletonBone width={50} height={11} />
    </View>
  </View>
);

/** Card variant: [type + date + property] + [amount] inside card */
const CardVariant: React.FC = () => (
  <View style={styles.card}>
    <View style={styles.cardLeft}>
      <SkeletonBone width={160} height={15} />
      <SkeletonBone width={90} height={13} />
      <SkeletonBone width={110} height={12} />
    </View>
    <View style={styles.cardRight}>
      <SkeletonBone width={75} height={17} />
    </View>
  </View>
);

/** Table-row variant: [Description] + [Posted] + [Amount] cells matching column flex */
const TableRowVariant: React.FC = () => (
  <View style={styles.tableRow}>
    <View style={styles.cellDescription}>
      <SkeletonBone width="80%" height={14} />
    </View>
    <View style={styles.cellPosted}>
      <SkeletonBone width={70} height={14} />
    </View>
    <View style={styles.cellAmount}>
      <SkeletonBone width={60} height={14} />
    </View>
  </View>
);

export const TransactionRowSkeleton: React.FC<TransactionRowSkeletonProps> = React.memo(
  ({ variant = 'row' }) => {
    switch (variant) {
      case 'card':
        return <CardVariant />;
      case 'table-row':
        return <TableRowVariant />;
      default:
        return <RowVariant />;
    }
  },
);

TransactionRowSkeleton.displayName = 'TransactionRowSkeleton';

const styles = StyleSheet.create({
  // Row variant
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingVertical: 16,
  },
  rowContent: {
    flex: 1,
    gap: 6,
  },
  rowAmountCol: {
    alignItems: 'flex-end',
    gap: 6,
  },

  // Card variant
  card: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 16,
    borderRadius: 12,
  },
  cardLeft: {
    flex: 1,
    gap: 6,
    marginRight: 12,
  },
  cardRight: {
    alignItems: 'flex-end',
    justifyContent: 'center',
  },

  // Table-row variant
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  cellDescription: {
    flex: 2,
  },
  cellPosted: {
    flex: 1,
  },
  cellAmount: {
    flex: 1,
  },
});
